"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, SearchCheck, FlaskConical, ShieldAlert, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ChallengeItem {
  id: number;
  title: string;
  category: string;
  description: string;
  point1: string;
  point2: string;
  linkUrl: string;
  linkLabel: string;
  iconName: "alert" | "search" | "flask" | "shield";
}
const challengeItems: ChallengeItem[] = [{
  id: 1,
  title: "On-Scene Chemical Spill Screening",
  category: "Rapid Response",
  description: "Rapidly identify bulk hazardous liquids, volatile compounds, and unknown contaminants directly through packaging or open surfaces to initiate immediate containment.",
  point1: "Non-destructive testing through translucent containers",
  point2: "High-confidence spectral matching, timing application dependent",
  linkUrl: "/SafetyAndSecurityPage",
  linkLabel: "View Spill Screening Solutions",
  iconName: "alert"
}, {
  id: 2,
  title: "Unknown Substance Triage",
  category: "Border & Defense",
  description: "Equip frontline operators, border forces, and bomb squads with definitive identification capabilities for synthetic narcotics, explosives, and active precursors.",
  point1: "Comprehensive libraries for opioids, fentanyl, and energetic materials",
  point2: "Automated mixture analysis for masked or adulterated threats",
  linkUrl: "/BorderSecurityAndDefencePage",
  linkLabel: "Explore Interdiction Tech",
  iconName: "search"
}, {
  id: 3,
  title: "Toxic Industrial Chemical Verification",
  category: "Industrial Safety",
  description: "Distinguish benign raw materials from corrosive, toxic, or flammable industrial chemicals during plant incidents, transport inspections, and storage audits.",
  point1: "Direct sampling without requiring sample preparation",
  point2: "Clear hazard class warning flags and classification tags",
  linkUrl: "/SafetyAndSecurityPage",
  linkLabel: "Review Chemical Verification",
  iconName: "flask"
}, {
  id: 4,
  title: "PPE & Containment Decision Support",
  category: "Tactical Advisory",
  description: "Translate complex spectroscopy readouts into decisive operational guidance for personal protective gear levels, hot-zone perimeters, and neutralisation steps.",
  point1: "Integrated action protocols aligned with emergency response guides",
  point2: "Real-time sync and report export for incident commanders",
  linkUrl: "/BorderSecurityAndDefencePage",
  linkLabel: "Discover Decision Support",
  iconName: "shield"
}];
export default function Challenges() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: ChallengeItem["iconName"]) => {
    switch (iconName) {
      case "alert":
        return <AlertTriangle className="size-5 text-primary" />;
      case "search":
        return <SearchCheck className="size-5 text-primary" />;
      case "flask":
        return <FlaskConical className="size-5 text-primary" />;
      case "shield":
        return <ShieldAlert className="size-5 text-primary" />;
      default:
        return <AlertTriangle className="size-5 text-primary" />;
    }
  };
  return <section id="criticalchemicalchallenges" data-nav="light" className="bg-muted py-24 md:py-32">
      <div className="page-container">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-background border-border">
            Tactical &amp; Industrial Preparedness
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Addressing Critical On-Scene Chemical Challenges
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            High-risk environments require instantaneous, definitive answers. Our specialized analytical systems 
            empower emergency personnel, industrial teams, and defence operators to safely isolate hazards and neutralize risks.
          </p>
        </div>

        {/* 2x2 Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {challengeItems.map((challenge, i) => <motion.div key={challenge.id} data-index={i} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border-l-4 border-l-primary border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,transform] duration-300 hover:-translate-y-1 rounded-xl">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                        {getIcon(challenge.iconName)}
                      </div>
                      <Badge variant="secondary" className="font-default text-xs font-medium">
                        {challenge.category}
                      </Badge>
                    </div>
                    <CardTitle className="font-default text-xl md:text-2xl font-semibold text-card-foreground">
                      {challenge.title}
                    </CardTitle>
                    <CardDescription className="font-default text-muted-foreground text-sm leading-relaxed mt-2">
                      {challenge.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-4">
                    <ul className="space-y-2.5">
                      <li className="flex items-start gap-2 text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{challenge.point1}</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{challenge.point2}</span>
                      </li>
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6 border-t border-border/60">
                  <Button asChild variant="ghost" className="w-full justify-between p-0 h-auto font-medium text-foreground hover:text-primary hover:bg-transparent">
                    <Link to={challenge.linkUrl} className="flex items-center justify-between w-full group py-1">
                      <span>{challenge.linkLabel}</span>
                      <ArrowRight className="size-4 text-primary transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}